"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="contact" ref={ref} className="bg-background py-24 px-4 md:px-8" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1)"
  }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            Get in Touch
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Have questions about our Classes? We're here to help you on your professional educational journey.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {}
          <div className="space-y-6">
            <FormProvider className="space-y-6" formId="contact_form_home" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out! We'll respond to your inquiry within 24 hours.">
              <ContactFormFields />
            </FormProvider>

            <Separator className="my-8" />

            {}
            <div className="space-y-4">
              <h3 className="font-display text-xl font-bold text-foreground mb-4">
                Contact Information
              </h3>
              
              <div className="flex items-start gap-3">
                <MapPin className="size-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-foreground">Address</p>
                  <p className="text-muted-foreground text-sm">PLOT NO.3, SUNDARA ILLAM, ANANDARAJA NAGAR 1ST CROSS, S.KODIKULAM, MADURAI - 625007.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="size-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-foreground">Phone</p>
                  <p className="text-muted-foreground text-sm">94864 88262</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="size-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-foreground">Email</p>
                  <p className="text-muted-foreground text-sm">ssofcommerce@gmail.com</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="relative h-[600px] rounded-md overflow-hidden border border-border shadow-soft">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1545674/img/education_building_architecture.png" alt="EduSphere campus building" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/10 backdrop-blur-[2px]"></div>
            <div className="absolute bottom-8 left-8 right-8 glass-effect p-6">
              <h3 className="font-display text-2xl font-bold text-foreground mb-2">
                Visit Our Campus
              </h3>
              <p className="text-muted-foreground">Schedule a tour to meet our dedicated faculty.</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary rounded-md text-center">
        <div className="inline-flex items-center justify-center size-16 bg-primary rounded-full mb-4">
          <Mail className="size-8 text-foreground-primary" />
        </div>
        <p className="text-foreground text-lg font-medium mb-2">Message Sent!</p>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="btn-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Name must not exceed 50 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Your Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="transition-all duration-[80ms] ease-out focus:border-primary focus:scale-[1.005]" />
            </FormControl>
            <FormDescription>First and last name</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="transition-all duration-[80ms] ease-out focus:border-primary focus:scale-[1.005]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[\d\s\-\+\(\)]+$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="transition-all duration-[80ms] ease-out focus:border-primary focus:scale-[1.005]" />
            </FormControl>
            <FormDescription>Optional - we may use it for follow-up</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Inquiry Type</FormLabel>
            <FormControl>
              <select {...field} className="border-input placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px] focus:scale-[1.005] duration-[80ms] ease-out">
                <option value="">Select an option</option>
                <option value="Admissions">Admissions</option>
                <option value="Academic Programs">Academic Programs</option>
                <option value="Faculty & Mentorship">Faculty & Mentorship</option>
                <option value="General Question">General Question</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      },
      maxLength: {
        value: 1000,
        message: "Message must not exceed 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your inquiry..." {...field} className="min-h-32 transition-all duration-[80ms] ease-out focus:border-primary focus:scale-[1.005]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary transition-all duration-[80ms] ease-out hover:scale-[1.02] active:scale-100">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}